import { useEffect, useRef } from "react";
import { Mic, Square, Pause, Play, RotateCcw, AlertTriangle, Send, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/api";
import { LiveTranscriptPanel } from "./LiveTranscript";
import { SourcePicker } from "./SourcePicker";
import { InterruptBanner } from "./InterruptBanner";
import { useRecorder } from "@/context/RecorderContext";

function useWave(analyser, ref) {
  useEffect(() => {
    if (!analyser) return;
    let raf;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      const c = ref.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      analyser.getByteFrequencyData(data);
      ctx.clearRect(0, 0, c.width, c.height);
      const bars = 56, w = c.width / bars;
      for (let i = 0; i < bars; i++) {
        const h = Math.max(4, (data[Math.floor((i / bars) * data.length * 0.6)] / 255) * c.height);
        ctx.fillStyle = "#7C3AED";
        ctx.fillRect(i * w + 2, (c.height - h) / 2, w - 4, h);
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [analyser, ref]);
}

export const Recorder = ({ onSubmit, busy, consent }) => {
  const r = useRecorder();
  const { L, supported, state, setState, wake, elapsed, analyser, blob, setBlob, error, recovered, setRecovered, hiddenWarn, lost, gap, setGap,
    mode, setMode, support, live, start, pause, resume, stop, reset, reconnect, clearChunks } = r;
  const canvas = useRef(null);
  useWave(analyser, canvas);
  const send = async (b) => { if (await onSubmit(b)) { await clearChunks(); reset(); r.form[2](); } };

  if (!supported)
    return <div className="flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="recorder-unsupported"><AlertTriangle size={18} /> {L("Pelayar ini tidak menyokong rakaman audio. Gunakan Chrome, Edge, Firefox atau Safari terkini, atau muat naik fail.", "This browser does not support audio recording. Use a recent Chrome, Edge, Firefox or Safari, or upload a file.")}</div>;

  return (
    <>
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="recorder">
      {recovered && state === "idle" && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sky-50 p-3 text-sm text-sky-800" data-testid="recovered-recording">
          {L("Rakaman terdahulu yang belum dihantar ditemui.", "An unsent previous recording was found.")}
          <span className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setBlob(recovered); setState("stopped"); setRecovered(null); }} data-testid="restore-recording-btn">{L("Pulihkan", "Restore")}</Button>
            <Button size="sm" variant="ghost" onClick={() => { clearChunks(); setRecovered(null); }} data-testid="discard-recording-btn">{L("Buang", "Discard")}</Button>
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`h-3 w-3 rounded-full ${state === "recording" ? "rec-pulse bg-red-500" : state === "paused" ? "bg-amber-400" : "bg-slate-300"}`} aria-hidden />
          <span className="text-sm font-semibold text-slate-700" data-testid="recorder-state" aria-live="polite">
            {lost && state === "recording" ? <span className="text-red-600">{L("Rakaman terganggu", "Recording interrupted")}</span> : { idle: L("Sedia merakam", "Ready to record"), recording: L("Sedang merakam…", "Recording…"), paused: L("Dijeda", "Paused"), stopped: L("Rakaman tamat — semak sebelum hantar", "Recording finished — review before sending") }[state]}
          </span>
        </div>
        <span className="font-mono text-3xl font-medium" data-testid="recorder-timer">{fmtTime(elapsed)}</span>
      </div>
      {state === "idle" && <div className="mt-5"><SourcePicker mode={mode} setMode={setMode} support={support} /></div>}
      <div className="mt-5 h-24 rounded-xl bg-slate-50">
        {analyser ? <canvas ref={canvas} width={900} height={96} className="h-full w-full" /> : <div className="grid h-full place-items-center text-xs text-slate-400">{L("Gelombang suara dipaparkan semasa merakam", "Waveform appears while recording")}</div>}
      </div>
      {["recording", "paused"].includes(state) && (wake === "on"
        ? <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="wake-lock-on"><Sun size={13} /> {L("Skrin kekal menyala semasa merakam", "Screen stays on while recording")}</p>
        : <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-800" data-testid="wake-lock-unsupported"><Sun size={13} /> {L("Pelayar ini tidak dapat mengekalkan skrin menyala — jangan kunci telefon semasa merakam.", "This browser can't keep the screen on — don't lock your phone while recording.")}</p>)}
      <InterruptBanner lost={lost} gap={gap} onReconnect={reconnect} onSend={stop} onDismiss={() => setGap(0)} />
      {hiddenWarn && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="background-warning">{L("Tab disembunyikan. Sesetengah pelayar (terutamanya iOS) menghentikan rakaman apabila tab digantung atau telefon dikunci. Pastikan skrin kekal hidup.", "Tab hidden. Some browsers (especially iOS) stop recording when the tab is suspended or the phone is locked. Keep the screen on.")}</p>}
      {error && <p className="mt-3 flex gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700" data-testid="recorder-error"><AlertTriangle size={16} /> {error}</p>}
      {blob && <audio controls src={URL.createObjectURL(blob)} className="mt-4 w-full" data-testid="recorder-preview" />}
      {state === "stopped" && blob && consent}
      <div className="mt-5 flex flex-wrap gap-3">
        {state === "idle" && <Button onClick={start} className="h-11 rounded-xl bg-red-500 px-6 hover:bg-red-600" data-testid="start-recording-btn"><Mic size={18} className="mr-1" /> {L("Mula rakam", "Start recording")}</Button>}
        {state === "recording" && <Button onClick={pause} variant="outline" className="h-11 rounded-xl" data-testid="pause-recording-btn"><Pause size={18} className="mr-1" /> {L("Jeda", "Pause")}</Button>}
        {state === "paused" && <Button onClick={resume} variant="outline" className="h-11 rounded-xl" data-testid="resume-recording-btn"><Play size={18} className="mr-1" /> {L("Sambung", "Resume")}</Button>}
        {["recording", "paused"].includes(state) && <Button onClick={stop} className="h-11 rounded-xl bg-slate-900 px-6" data-testid="stop-recording-btn"><Square size={16} className="mr-1" /> {L("Henti", "Stop")}</Button>}
        {state === "stopped" && blob && (
          <>
            <Button onClick={() => send(blob)} disabled={busy} className="h-11 rounded-xl bg-violet-600 px-6 hover:bg-violet-700" data-testid="submit-recording-btn"><Send size={16} className="mr-1" /> {L("Hantar untuk diproses", "Send for processing")}</Button>
            <Button onClick={reset} variant="ghost" className="h-11 rounded-xl" data-testid="reset-recording-btn"><RotateCcw size={16} className="mr-1" /> {L("Rakam semula", "Re-record")}</Button>
          </>
        )}
      </div>
    </div>
    <LiveTranscriptPanel live={live} state={state} elapsed={elapsed} />
    </>
  );
};
